import {
  RecordValueConfigurationContext,
  RecordValueConfigurationContextProvider,
} from "#batteries-included-components/Layouts/RecordConfiguration/RecordConfigurationContext";
import { useFile } from "#src/hooks/useFile";
import {
  Banner,
  Button,
  Dialog,
  FileUploadInput,
  Form,
  Icon,
  Page,
  TextInput,
  Tooltip,
  useForm,
  useToast,
} from "@validereinc/common-components";
import {
  FileUploadedSchema,
  RecordDomain,
  RecordValueConfigurationType,
  RecordValueUpdateType,
  isRecordValueLocked as isRecordValueLockedFunction,
} from "@validereinc/domain";
import { BaseError } from "@validereinc/utilities";
import classNames from "classnames/bind";
import React, { useContext, useState } from "react";
import { RecordConfigurationPanel } from "../RecordConfigurationPanel";
import styles from "./RecordValueSourceConfigurationPage.module.scss";

const cx = classNames.bind(styles);

export type RecordConfigurationFormType = RecordValueConfigurationType &
  Pick<RecordValueUpdateType, "measurement_unit">;

export type RecordValueSourceConfigurationPagePropsType = {
  pageProps: any; // TODO: Get this type from common-components
  onClose: () => void;
};

export const RecordValueSourceConfigurationPageContent = ({
  pageProps,
  onClose,
}: RecordValueSourceConfigurationPagePropsType) => {
  const { toast } = useToast();
  const [isNoteModalOpen, setIsNoteModalOpen] = useState(false);
  const noteForm = useForm({});
  const { record, recordValue, isLoading, form } =
    useContext(RecordValueConfigurationContext) ?? {};
  const { handleFileUpload, handleFileDownload } = useFile();

  const handleSubmit = async ({
    measurement_unit,
    ...configuration
  }: RecordConfigurationFormType) => {
    if (!record?.id || !recordValue?.measurement_type) {
      return;
    }

    let body = {
      measurement_unit,
      configuration,
    } as object;

    if (configuration.value) {
      configuration.value = Number(configuration.value);
    }

    const noteFormValues = noteForm.getValues();

    if (noteFormValues?.attachment?.at(0)) {
      try {
        const attachmentValue = noteFormValues?.attachment?.at(0);

        if (attachmentValue) {
          const attachmentValueVerified =
            FileUploadedSchema.safeParse(attachmentValue);

          if (!attachmentValueVerified.success)
            throw new BaseError(
              "Attachment value not a file uploaded representation."
            );

          body = {
            ...body,
            attachment_filename: attachmentValueVerified.data.name,
            attachment_ref: attachmentValueVerified.data.ref,
          };
        }
      } catch (e) {
        toast.push({
          intent: "error",
          description: "Unable to upload attachment",
        });
        return;
      }
    }

    if (noteFormValues?.note?.trim()) {
      body = { ...body, note: noteFormValues.note.trim() };
    }

    try {
      await RecordDomain.recordValue.update(
        {
          recordId: record?.id,
          measurementType: recordValue?.measurement_type,
        },
        body as RecordValueUpdateType
      );
      onClose?.();
    } catch (err) {
      console.error(err);
      toast.push({
        intent: "error",
        description: "Unable to update record configuration",
      });
    }
  };

  const isRecordValueLocked =
    !!recordValue && isRecordValueLockedFunction(recordValue);

  const footer = (
    <>
      <Button
        onClick={onClose}
        disabled={form?.formState.isSubmitting}
      >
        Cancel
      </Button>
      <SaveButton
        isLocked={isRecordValueLocked}
        onClick={() => setIsNoteModalOpen(true)}
        isDisabled={!!isLoading || !!form?.formState.isSubmitting}
      />
    </>
  );

  const renderNotesModal = () => (
    <Dialog
      title="Include Note"
      isOpen={isNoteModalOpen}
      onClose={() => {
        if (form?.formState.isSubmitting) return;
        setIsNoteModalOpen(false);
      }}
      actionRow={[
        <Button
          onClick={form?.handleSubmit(handleSubmit)}
          variant="primary"
          disabled={form?.formState.isSubmitting}
          key="done-button"
        >
          Done
        </Button>,
      ]}
    >
      <Form {...noteForm}>
        <TextInput
          name="note"
          label="Note"
          isRequired={false}
        />

        <FileUploadInput
          name="attachment"
          id="attachment"
          label="Attachment"
          placeholder="You can drag a file here to use as an attachment"
          isRequired={false}
          uploadFile={handleFileUpload}
          downloadFile={handleFileDownload}
        />
      </Form>
    </Dialog>
  );

  if (!form) {
    return null;
  }

  return (
    <Page
      {...pageProps}
      isLoading={isLoading}
      footer={footer}
    >
      <Form
        {...form}
        onSubmit={() => {
          form?.handleSubmit(handleSubmit)();
        }}
      >
        {isRecordValueLocked && (
          <Banner
            titleText="This record value is locked and cannot be edited."
            variant="generic"
            icon={<Icon variant="lock-simple" />}
            className={cx("lockedBanner")}
          />
        )}
        <RecordConfigurationPanel />
        {renderNotesModal()}
      </Form>
    </Page>
  );
};

export const RecordValueSourceConfigurationPage = (
  props: RecordValueSourceConfigurationPagePropsType
) => {
  return (
    <RecordValueConfigurationContextProvider>
      <RecordValueSourceConfigurationPageContent {...props} />
    </RecordValueConfigurationContextProvider>
  );
};

function SaveButton({
  isLocked,
  onClick,
  isDisabled,
}: {
  isLocked: boolean;
  onClick: () => void;
  isDisabled: boolean;
}) {
  const button = (
    <Button
      variant="primary"
      onClick={onClick}
      disabled={isDisabled || isLocked}
      type="button"
      icon={isLocked ? "lock" : undefined}
    >
      Save
    </Button>
  );

  if (isLocked) {
    return (
      <Tooltip
        content="You cannot edit because the record is locked."
        title="Locked Record Value"
      >
        {button}
      </Tooltip>
    );
  }
  return button;
}
